import { lazy, Suspense, useMemo, useState } from 'react';
import { ArrowForwardRounded } from '@mui/icons-material';
import CardActionArea from '@mui/material/CardActionArea';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import RelatedQuestions from '../../../Completion/components/RelatedQuestions';
import { RichEndingContext } from '../../../Completion/components/RichEndings/useRichEndingContext';
import RelatedReading from '../../../Completion/components/RelatedReading';

import useInterviewType from 'src/interviews/hooks/useInterviewType';
import useResults from 'src/interviews/hooks/useResults';
import useImpressionTracking from 'src/hooks/useImpressionTracking';
import useInterview from 'src/interviews/hooks/useInterview';

import { trackTreatmentPlanButtonClicked, trackTreatmentPlanButtonViewed } from '../../analytics';

import type { RelatedReading as RelatedReadingType } from '../../../Completion/components/RichEndings/types';
import type { IDiagnosis, ITreatmentPlan, TreatmentSupplier } from 'src/packages/polaris/types';
import ResponsiveSurface from '../../../ResponsiveSurface';

const TreatmentPlanBody = lazy(() => import('./Body'));

export interface ITreatmentPlanProps {
  differential: IDiagnosis;
}

const DefaultTreatmentPlan = ({ differential }: ITreatmentPlanProps) => {
  const [relatedReadings, setRelatedReadings] = useState<RelatedReadingType[]>();
  const [treatmentPlan, setTreatmentPlan] = useState<ITreatmentPlan>();
  const [treatmentSuppliers, setTreatmentSuppliers] = useState<TreatmentSupplier[]>();

  const interviewType = useInterviewType();
  const interviewResults = useResults();
  const { token } = useInterview();

  const analyticsProps = useMemo(
    () => ({
      interviewToken: token || '',
      interviewType,
      conditionName: differential.name,
      predictorId: differential.predictor,
    }),
    [differential.name, differential.predictor, interviewType, token],
  );

  /*
    Since we only want related questions, readings, and suppliers relevant to this specific dx,
    we filter out other dx's that may appear in the results
  */
  const result = useMemo(
    () =>
      interviewResults
        ? {
            ...interviewResults,
            differential: interviewResults?.differential?.filter(
              (dx) => dx.token === differential.token,
            ),
          }
        : undefined,
    [differential.token, interviewResults],
  );

  const [showTreatmentPlan, setShowTreatmentPlan] = useState(false);

  const handleViewTreatmentPlanClick = () => {
    trackTreatmentPlanButtonClicked(analyticsProps);
    setShowTreatmentPlan(true);
  };

  const closeTreatmentPlan = () => {
    setShowTreatmentPlan(false);
  };

  const impressionRef = useImpressionTracking<HTMLButtonElement>(() => {
    trackTreatmentPlanButtonViewed(analyticsProps);
  });

  return (
    <>
      <CardActionArea
        onClick={handleViewTreatmentPlanClick}
        className="bp-flex bp-justify-between bp-rounded-none bp-bg-blue-0 bp-px-3 bp-py-2 bp-text-blue-50"
        ref={impressionRef}
      >
        <Typography variant="body2" className="bp-text-xs bp-text-blue-50">
          {`View your treatment plan`}
        </Typography>
        <ArrowForwardRounded color="primary" fontSize="small" />
      </CardActionArea>
      <ResponsiveSurface
        isOpen={showTreatmentPlan}
        onClose={closeTreatmentPlan}
        modalHeader="Your treatment plan"
        backText="Back to results"
      >
        <Grid container spacing={2} direction="column" padding={2} paddingTop={1}>
          <Suspense fallback={null}>
            <RichEndingContext.Provider
              value={{
                relatedReadings,
                setRelatedReadings,
                setTreatmentPlan,
                setTreatmentSuppliers,
                treatmentPlan,
                treatmentSuppliers,
              }}
            >
              {result && (
                <TreatmentPlanBody
                  differential={differential}
                  goBack={closeTreatmentPlan}
                  interviewType={interviewType}
                  result={result}
                />
              )}
              {result && <RelatedQuestions interviewType={interviewType} result={result} />}
              {result && <RelatedReading interviewType={interviewType} result={result} />}
            </RichEndingContext.Provider>
          </Suspense>
        </Grid>
      </ResponsiveSurface>
    </>
  );
};

export default DefaultTreatmentPlan;
